import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { cn } from '../../../components/ui/Button';
import { generateCalendarLinks } from '../../../utils/calendarUtils';
import { 
  format, addMonths, subMonths, startOfMonth, endOfMonth, 
  startOfWeek, endOfWeek, isSameMonth, addDays, addWeeks, subWeeks, isToday 
} from 'date-fns';

const EVENT_COLORS = [
  'bg-blue-50 text-blue-700 border-l-blue-500',
  'bg-emerald-50 text-emerald-700 border-l-emerald-500',
  'bg-violet-50 text-violet-700 border-l-violet-500',
  'bg-amber-50 text-amber-700 border-l-amber-500',
  'bg-rose-50 text-rose-700 border-l-rose-500',
];

const HOURS = Array.from({ length: 15 }).map((_, i) => i + 8); // 8 AM to 10 PM
const HOUR_HEIGHT = 100;

export default function BarberSchedule() {
  const navigate = useNavigate();
  const [view, setView] = useState('Month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle responsive check
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('bookings_1740423033529')
          .select(`*, customer:customer_id(full_name, phone), service:service_id(name, duration, price)`)
          .eq('barber_id', user.id)
          .neq('status', 'cancelled');
        setBookings(data || []);
      }
      setLoading(false);
    };
    fetchBookings();
  }, []);

  const serviceColors = useMemo(() => {
    const map = {};
    let colorIndex = 0;
    const uniqueServices = [...new Set(bookings.map(b => b.service?.name).filter(Boolean))];
    uniqueServices.forEach(name => {
      map[name] = EVENT_COLORS[colorIndex % EVENT_COLORS.length];
      colorIndex++;
    });
    return map;
  }, [bookings]);

  const dateLabel = useMemo(() => {
    if (isMobile) {
      const end = addDays(currentDate, 2);
      return `${format(currentDate, 'MMM d')} – ${format(end, isSameMonth(currentDate, end) ? 'd, yyyy' : 'MMM d, yyyy')}`;
    }
    if (view === 'Month') return format(currentDate, 'MMMM yyyy');
    if (view === 'Week') {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return `${format(start, 'MMM d')} – ${format(end, isSameMonth(start, end) ? 'd, yyyy' : 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'MMMM d, yyyy');
  }, [currentDate, view, isMobile]);

  const handleOpenMaps = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
  };

  const format12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hours = parseInt(h);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${m} ${ampm}`;
  };

  const navigateDate = (direction) => {
    const amount = direction === 'next' ? 1 : -1;
    if (isMobile) {
      // Mobile always steps by 3 days
      setCurrentDate(prev => addDays(prev, amount * 3));
      return;
    }
    if (view === 'Month') {
      setCurrentDate(prev => amount === 1 ? addMonths(prev, 1) : subMonths(prev, 1));
    } else if (view === 'Week') {
      setCurrentDate(prev => amount === 1 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    } else {
      setCurrentDate(prev => amount === 1 ? addDays(prev, 1) : addDays(prev, -1));
    }
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    
    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const isCurrentMonth = isSameMonth(cloneDay, monthStart);
        const dayBookings = bookings.filter(b => b.booking_date === format(cloneDay, 'yyyy-MM-dd'));
        
        days.push(
          <div 
            key={cloneDay.toString()} 
            className={cn(
              "min-h-[140px] p-2 border-r border-b border-gray-100 relative group cursor-default",
              !isCurrentMonth ? "bg-gray-50/40" : "bg-white"
            )}
          >
            <span className={cn(
              "text-[10px] font-black w-6 h-6 flex items-center justify-center rounded-full mb-1 transition-all",
              isToday(cloneDay) ? "bg-brand-primary text-white shadow-lg scale-110" : (isCurrentMonth ? "text-gray-700" : "text-gray-300")
            )}>
              {format(cloneDay, 'd')}
            </span>
            <div className="space-y-1 overflow-y-auto max-h-[100px] hide-scrollbar pb-2">
              {dayBookings.map(b => (
                <div 
                  key={b.id} 
                  onClick={() => setSelectedBooking(b)}
                  className={cn(
                    "text-[8px] p-1.5 rounded-lg border-l-2 truncate font-black uppercase tracking-tight cursor-pointer hover:brightness-95 hover:translate-x-1 transition-all",
                    serviceColors[b.service?.name] || EVENT_COLORS[0]
                  )}
                >
                  {b.booking_time.slice(0, 5)} {b.service?.name.split(' ')[0]} - {b.customer?.full_name.split(' ')[0]}
                </div>
              ))}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(<div className="grid grid-cols-7" key={day.toString()}>{days}</div>);
      days = [];
    }

    return (
      <div className="border-t border-l border-gray-100 rounded-[2.5rem] overflow-hidden shadow-sm">
        <div className="grid grid-cols-7 bg-gray-50/80">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="p-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-r border-b border-gray-100">{d}</div>
          ))}
        </div>
        {rows}
      </div>
    );
  };

  const renderTimeGrid = (daysToRender) => {
    return (
      <div className="flex bg-white border border-gray-100 rounded-[2.5rem] overflow-hidden shadow-sm">
        <div className="w-16 sm:w-20 flex-shrink-0 border-r border-gray-100 bg-gray-50/20 pt-14">
          {HOURS.map(h => (
            <div key={h} className="text-[9px] sm:text-[10px] text-gray-400 font-black text-right pr-2 sm:pr-4 relative" style={{ height: `${HOUR_HEIGHT}px` }}>
              <span className="absolute -top-2 right-2 sm:right-4">{h > 12 ? `${h-12} PM` : h === 12 ? '12 PM' : `${h} AM`}</span>
            </div>
          ))}
        </div>
        <div className={cn("flex-1 grid divide-x divide-gray-100", daysToRender.length === 1 ? "grid-cols-1" : daysToRender.length === 3 ? "grid-cols-3" : "grid-cols-7")}>
          {daysToRender.map((day, i) => {
            const dayBookings = bookings.filter(b => b.booking_date === format(day, 'yyyy-MM-dd'));
            return (
              <div key={i} className="relative">
                <div className="h-14 flex flex-col items-center justify-center border-b border-gray-100 sticky top-0 bg-white/90 backdrop-blur-md z-20">
                  <span className="text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">{format(day, 'EEE')}</span>
                  <span className={cn("text-xs sm:text-sm font-black", isToday(day) ? "text-brand-primary" : "text-gray-700")}>{format(day, 'd')}</span>
                </div>
                <div className="relative" style={{ height: `${HOURS.length * HOUR_HEIGHT}px` }}>
                  {HOURS.map(h => <div key={h} className="border-b border-gray-50 absolute w-full" style={{ top: `${(h - HOURS[0]) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }} />)}
                  {dayBookings.map(b => {
                    const [h, m] = b.booking_time.split(':').map(Number);
                    const top = (((h - HOURS[0]) * 60) + m) / 60 * HOUR_HEIGHT;
                    const durationMins = parseInt(b.service?.duration) || 45;
                    const height = (durationMins / 60) * HOUR_HEIGHT;
                    return (
                      <div 
                        key={b.id} 
                        onClick={() => setSelectedBooking(b)}
                        className={cn(
                          "absolute left-0.5 right-0.5 sm:left-1 sm:right-1 rounded-lg sm:rounded-xl border-l-2 sm:border-l-4 p-1 sm:p-3 shadow-lg z-10 transition-all hover:scale-[1.02] cursor-pointer",
                          serviceColors[b.service?.name] || EVENT_COLORS[0]
                        )}
                        style={{ top: `${top}px`, height: `${height - 2}px` }}
                      >
                        <div className="flex flex-col h-full justify-between">
                          <div className="overflow-hidden">
                            <p className="text-[9px] sm:text-[11px] font-black leading-tight truncate uppercase tracking-tight">{b.service?.name}</p>
                            <p className="text-[8px] sm:text-[10px] opacity-80 font-bold truncate mt-0.5">{b.customer?.full_name}</p>
                          </div>
                          <div className="hidden sm:flex items-center gap-1 opacity-60 text-[9px] font-black">
                            <SafeIcon name="Clock" className="text-[10px]" /> {format12h(b.booking_time)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="animate-in fade-in duration-500">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Schedule</h1>
          <p className="text-gray-500 font-medium italic mt-1">Real-time grooming timeline and client management.</p>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] sm:rounded-[3rem] border border-gray-100 shadow-sm overflow-hidden mb-10">
        <div className="p-4 sm:p-6 flex flex-col xl:flex-row justify-between items-center gap-6 border-b border-gray-100 bg-gray-50/30">
          {/* Hide view switcher on mobile as requested */}
          {!isMobile && (
            <div className="flex bg-gray-200 p-1.5 rounded-2xl border border-gray-200">
              {['Day', 'Week', 'Month'].map(v => (
                <button 
                  key={v} 
                  onClick={() => setView(v)}
                  className={cn("px-10 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", view === v ? "bg-white text-brand-primary shadow-sm" : "text-gray-500 hover:text-gray-700")}
                >
                  {v}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 sm:gap-10 w-full sm:w-auto justify-between sm:justify-center">
            <button onClick={() => navigateDate('prev')} className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl sm:rounded-2xl transition-colors border border-gray-200 bg-white shadow-sm">
              <SafeIcon name="ChevronLeft" className="text-gray-600" />
            </button>
            <h2 className="text-lg sm:text-2xl font-black text-brand-dark min-w-[140px] sm:min-w-[240px] text-center tracking-tight">{dateLabel}</h2>
            <button onClick={() => navigateDate('next')} className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl sm:rounded-2xl transition-colors border border-gray-200 bg-white shadow-sm">
              <SafeIcon name="ChevronRight" className="text-gray-600" />
            </button>
          </div>

          <button 
            onClick={() => setCurrentDate(new Date())}
            className="w-full sm:w-auto px-10 py-4 bg-brand-dark text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-brand-dark/20 hover:bg-brand-black transition-all active:scale-95"
          >
            Today
          </button>
        </div>

        <div className="p-2 sm:p-6 overflow-x-auto">
          <div className={cn(isMobile ? "w-full" : "min-w-[1000px]")}>
            {loading ? (
              <div className="h-[400px] sm:h-[600px] flex items-center justify-center">
                <div className="w-14 h-14 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                {isMobile ? (
                  // Mobile view: Always show exactly 3 days
                  renderTimeGrid([
                    currentDate,
                    addDays(currentDate, 1),
                    addDays(currentDate, 2)
                  ])
                ) : (
                  <>
                    {view === 'Month' && renderMonthView()}
                    {view === 'Week' && renderTimeGrid(Array.from({ length: 7 }).map((_, i) => addDays(startOfWeek(currentDate), i)))}
                    {view === 'Day' && renderTimeGrid([currentDate])}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-brand-dark/80 z-[200] flex items-center justify-center p-4 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] sm:rounded-[3.5rem] w-full max-w-xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            
            <div className="p-6 sm:p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-4">
                <div className={cn("w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-[1.25rem] flex items-center justify-center shadow-sm", serviceColors[selectedBooking.service?.name] || EVENT_COLORS[0])}>
                  <SafeIcon name="Calendar" className="text-xl sm:text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-2xl font-black text-brand-dark tracking-tight">Appointment File</h3>
                  <p className="text-[8px] sm:text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">Ref No: {selectedBooking.id.slice(0, 12).toUpperCase()}</p>
                </div>
              </div>
              <button onClick={() => setSelectedBooking(null)} className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl sm:rounded-2xl transition-all">
                <SafeIcon name="X" className="text-xl sm:text-2xl text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 sm:p-10 space-y-6 sm:space-y-8 hide-scrollbar">
              
              <div className="bg-brand-primary/5 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-8 border border-brand-primary/10 grid grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-[8px] sm:text-[10px] font-black text-brand-primary uppercase tracking-[0.2em] mb-1">Scheduled Date</label>
                  <p className="text-sm sm:text-lg font-black text-brand-dark">{format(new Date(selectedBooking.booking_date), 'EEEE, MMMM do')}</p>
                </div>
                <div className="flex flex-col">
                  <label className="text-[8px] sm:text-[10px] font-black text-brand-primary uppercase tracking-[0.2em] mb-1">Arrival Time</label>
                  <p className="text-sm sm:text-lg font-black text-brand-dark">{format12h(selectedBooking.booking_time)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10">
                <div className="space-y-4">
                  <div>
                    <label className="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-1 sm:mb-2">Customer Profile</label>
                    <p className="text-lg sm:text-xl font-black text-brand-dark">{selectedBooking.customer?.full_name}</p>
                  </div>
                  <a href={`tel:${selectedBooking.customer?.phone}`} className="inline-flex items-center gap-3 px-4 sm:px-5 py-2 sm:py-3 bg-brand-primary/5 text-brand-primary rounded-xl text-xs sm:text-sm font-black hover:bg-brand-primary/10 transition-all">
                    <SafeIcon name="Phone" className="text-sm sm:text-lg" /> {selectedBooking.customer?.phone}
                  </a>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-1 sm:mb-2">Service Logic</label>
                    <p className="text-lg sm:text-xl font-black text-brand-dark">{selectedBooking.service?.name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xl sm:text-2xl font-black text-brand-dark">Rs. {Number(selectedBooking.service?.price || 0).toLocaleString()}</span>
                    <span className="w-1.5 h-1.5 bg-gray-200 rounded-full"></span>
                    <span className="text-[10px] sm:text-sm text-gray-500 font-bold uppercase tracking-widest">{selectedBooking.service?.duration}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-gray-100 shadow-inner relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <label className="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3">Service Location</label>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg sm:rounded-xl shadow-sm flex items-center justify-center flex-shrink-0">
                    <SafeIcon name="MapPin" className="text-brand-primary" />
                  </div>
                  <p className="text-sm sm:text-lg font-bold text-gray-700 leading-relaxed">{selectedBooking.address}</p>
                </div>
                
                <button 
                  onClick={() => handleOpenMaps(selectedBooking.address)}
                  className="w-full mt-6 sm:mt-8 py-4 sm:py-5 bg-brand-primary text-white rounded-xl sm:rounded-[1.5rem] font-black text-xs sm:text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-4 shadow-2xl shadow-brand-primary/30 hover:bg-brand-accent hover:scale-[1.01] transition-all active:scale-[0.98]"
                >
                  <SafeIcon name="Map" className="text-lg sm:text-xl" /> Get Directions Now
                </button>
              </div>

              <div>
                <label className="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-4 sm:mb-6 text-center">Sync to Service Calendar</label>
                <div className="grid grid-cols-3 gap-3 sm:gap-5">
                  {(() => {
                    const links = generateCalendarLinks(selectedBooking);
                    return (
                      <>
                        <a href={links.google} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 bg-white border border-gray-100 rounded-2xl sm:rounded-[1.75rem] hover:border-brand-primary hover:shadow-xl transition-all group">
                          <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-6 h-6 sm:w-8 sm:h-8" alt="Google" />
                          <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-brand-primary">Google</span>
                        </a>
                        <a href={links.apple} download="appointment.ics" className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 bg-white border border-gray-100 rounded-2xl sm:rounded-[1.75rem] hover:border-brand-primary hover:shadow-xl transition-all group">
                          <SafeIcon name="Laptop" className="text-2xl sm:text-3xl text-gray-700" />
                          <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-brand-primary">Apple</span>
                        </a>
                        <a href={links.outlook} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 bg-white border border-gray-100 rounded-2xl sm:rounded-[1.75rem] hover:border-brand-primary hover:shadow-xl transition-all group">
                          <SafeIcon name="Mail" className="text-2xl sm:text-3xl text-blue-600" />
                          <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-brand-primary">Outlook</span>
                        </a>
                      </>
                    );
                  })()}
                </div>
              </div>

            </div>

            <div className="p-6 sm:p-8 bg-gray-50 border-t border-gray-100 flex gap-3 sm:gap-4">
              <button 
                onClick={() => setSelectedBooking(null)}
                className="flex-1 py-4 sm:py-5 bg-white border border-gray-200 text-gray-500 font-black uppercase text-[10px] sm:text-xs tracking-[0.2em] rounded-xl sm:rounded-2xl hover:bg-gray-100 transition-all"
              >
                Close View
              </button>
              <button 
                onClick={() => { navigate(`/booking/b/${selectedBooking.id}`); setSelectedBooking(null); }}
                className="flex-[1.5] py-4 sm:py-5 bg-brand-dark text-white font-black uppercase text-[10px] sm:text-xs tracking-[0.2em] rounded-xl sm:rounded-2xl hover:bg-black transition-all shadow-lg"
              >
                Manage Live Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}